import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  InputLabel,
  Rating,
  Typography,
  LinearProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { SlLocationPin } from 'react-icons/sl';
import { TfiCommentAlt } from 'react-icons/tfi';
import { MdOutlineFavoriteBorder } from 'react-icons/md';
import { IoShareSocialOutline } from 'react-icons/io5';
import { IoMdHeart } from 'react-icons/io';
import OpenTag from '../../../Components/Common/OpenTag';

/**
 * Restaurant header component displaying name, location, rating, and action buttons
 * @param {Object} props - Component props
 * @param {Object} props.data - Restaurant data
 * @param {boolean} props.favLoading - Favorite loading state
 * @param {boolean} props.favSuccess - Favorite success state
 * @param {Function} props.onAddToFavorite - Add to favorite handler
 * @param {Function} props.onShare - Share handler
 * @param {boolean} props.isAuthenticated - Authentication status
 * @param {Function} props.onLoginRedirect - Login redirect handler
 */
const RestaurantHeader = ({
  data,
  favLoading,
  favSuccess,
  onAddToFavorite,
  onShare,
  isAuthenticated,
  onLoginRedirect,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!data) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        columnGap: 3,
        flexDirection: {
          lg: 'row',
          md: 'row',
          sm: 'column',
          xs: 'column',
        },
      }}
    >
      <Box
        sx={{
          width: { lg: '60%', md: '60%', sm: '100%', xs: '100%' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            columnGap: 3,
          }}
        >
          <InputLabel
            sx={{
              fontSize: {
                lg: '26px',
                md: '26px',
                sm: '24px',
                xs: '20px',
              },
              fontWeight: 700,
              lineHeight: '25px',
              color: '#151515',
            }}
          >
            {data?.RestaurantName}
          </InputLabel>
          {/* {data?.OpenFrom !== 'No reservation hour' && (
            <OpenTag hoursString={data?.OpenFrom} />
          )} */}
        </Box>

        <Box
          sx={{
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            columnGap: 2,
          }}
        >
          <SlLocationPin />
          <Typography
            sx={{
              fontSize: {
                lg: '14px',
                md: '14px',
                sm: '12px',
                xs: '10px',
              },
            }}
          >
            {data?.Address}
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 1,
            display: 'flex',
            alignItems: 'center',
            columnGap: 3,
          }}
        >
          <Rating
            readOnly
            value={parseInt(data?.ReviewDetails?.Rating)}
            precision={0.5}
            size="small"
            sx={{
              color: '#BC172F',
              fontSize: {
                lg: '14px',
                md: '14px',
                sm: '12px',
                xs: '12px',
              },
            }}
          />
          <Box
            sx={{
              display: 'flex',
              columnGap: 2,
              alignItems: 'center',
            }}
          >
            <TfiCommentAlt style={{ fontSize: isMobile ? '12px' : '15px' }} />
            <Typography sx={{ fontSize: '12px' }}>
              {data?.ReviewDetails?.Reviews?.length} reviews
            </Typography>
          </Box>
        </Box>

        <Typography
          sx={{
            mt: 1,
            fontWeight: 600,
            fontSize: {
              lg: '16px',
              md: '16px',
              sm: '14px',
              xs: '12px',
            },
          }}
        >
          <span style={{ fontWeight: 300 }}>From</span> ₦
          {data?.AverageCost?.toLocaleString()}
        </Typography>
      </Box>

      {!isMobile && (
        <Box>
          <Button
            onClick={() =>
              isAuthenticated ? onAddToFavorite() : onLoginRedirect()
            }
            startIcon={
              favSuccess ? (
                <IoMdHeart style={{ color: '#BC172F' }} />
              ) : (
                <MdOutlineFavoriteBorder />
              )
            }
            sx={{
              bgcolor: '#fae8ea',
              fontWeight: 400,
              border: '0.5px solid #BC172F',
            }}
          >
            {favLoading ? (
              <LinearProgress sx={{ width: '50px' }} />
            ) : (
              'Favourite'
            )}
          </Button>
          <Button
            onClick={onShare}
            startIcon={<IoShareSocialOutline />}
            sx={{
              bgcolor: '#fae8ea',
              fontWeight: 400,
              border: '0.5px solid #BC172F',
              ml: 3,
            }}
          >
            Share
          </Button>
        </Box>
      )}
    </Box>
  );
};

RestaurantHeader.propTypes = {
  data: PropTypes.object,
  favLoading: PropTypes.bool.isRequired,
  favSuccess: PropTypes.bool.isRequired,
  onAddToFavorite: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  onLoginRedirect: PropTypes.func.isRequired,
};

export default RestaurantHeader;
